/**
 * 렌더러 공통 유틸 + 타입 정의
 *
 * 모든 디자인 렌더러가 공유하는 함수, 컴포넌트, Props 인터페이스.
 */
import React, { useState, useEffect } from 'react';
import { t, plural, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import {
  Post, BoardType, IssueStatus,
} from '../../../models/Board';
import { AlertCircle, Info, CheckCircle2, Circle, MessageCircle } from 'lucide-react';
import { MyNotifs } from '../../../services/MyNotifs';

// ─── 공통 유틸 ────────────────────────────────────────────────

/** TipTap JSON content 에서 첫 이미지 URL 추출. 없으면 null.
 *  basecamp/discourse 렌더러의 썸네일 fallback 으로 사용. Post 모델 변경 없이 자동 추출. */
export function extractFirstImageUrl(content: any | null): string | null {
  if (!content || typeof content !== 'object') return null;
  const search = (node: any): string | null => {
    if (!node || typeof node !== 'object') return null;
    if (node.type === 'image' && typeof node.attrs?.src === 'string' && node.attrs.src.trim()) {
      return node.attrs.src;
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content) {
        const found = search(child);
        if (found) return found;
      }
    }
    return null;
  };
  return search(content);
}

/** 상대 시간 포맷 — lingui i18n.locale 에 맞춰 자동 번역.
 *  한국어/일본어는 plural form 단일이지만 영어는 1/N 구분. */
export function timeAgo(dateStr: string): string {
  const now = Date.now();
  const d = new Date(dateStr).getTime();
  const diff = now - d;
  const min = Math.floor(diff / 60000);
  if (min < 1) return t`Just now`;
  if (min < 60) return plural(min, { one: '# min ago', other: '# min ago' });
  const hr = Math.floor(min / 60);
  if (hr < 24) return plural(hr, { one: '# hr ago', other: '# hr ago' });
  const day = Math.floor(hr / 24);
  if (day < 30) return plural(day, { one: '# day ago', other: '# days ago' });
  const locale = i18n.locale || 'en';
  return new Date(dateStr).toLocaleDateString(locale === 'ko' ? 'ko-KR' : locale === 'ja' ? 'ja-JP' : 'en-US', {
    month: 'short', day: 'numeric',
  });
}

/** 날짜 포맷 (YY.MM.DD) — 현재 locale 의 로케일로 표시 */
export function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const locale = i18n.locale || 'en';
  return d.toLocaleDateString(locale === 'ko' ? 'ko-KR' : locale === 'ja' ? 'ja-JP' : 'en-US', {
    year: '2-digit', month: '2-digit', day: '2-digit',
  });
}

// 아바타는 표준 컴포넌트 `<UserAvatar>` (../../components/UserAvatar) 사용.
// 이전 shared.Avatar / getInitial 은 비표준 (rounded-full + initial fallback) 이었어서 제거.

// ─── 중요도 뱃지 ─────────────────────────────────────────────

/** notice 게시판에서 metadata.importance에 따른 뱃지 */
export function ImportanceBadge({ post }: { post: Post }) {
  const importance = post.metadata?.importance as string | undefined;
  if (!importance) return null;
  if (importance === 'critical') {
    return (
      <span className="shrink-0 inline-flex items-center gap-0.5 text-meta-sm font-semibold px-1.5 py-0.5 rounded bg-destructive/15 text-destructive">
        <AlertCircle className="size-3" />
        <Trans>Important</Trans>
      </span>
    );
  }
  if (importance === 'info') {
    return (
      <span className="shrink-0 inline-flex items-center gap-0.5 text-meta-sm font-semibold px-1.5 py-0.5 rounded bg-warning/15 text-warning">
        <Info className="size-3" />
        <Trans>Notice</Trans>
      </span>
    );
  }
  return null;
}

// ─── 고정글 분리 유틸 ──────────────────────────────────────────

export function splitPinnedPosts(posts: Post[]) {
  const pinned = posts.filter(p => p.is_pinned);
  const normal = posts.filter(p => !p.is_pinned);
  return { pinned, normal };
}

// ─── QnA 해결 상태 뱃지 ──────────────────────────────────────

export function QnaStatusBadge({ post }: { post: Post }) {
  const isResolved = post.metadata?.is_resolved === true;
  if (isResolved) {
    return (
      <span className="shrink-0 inline-flex items-center gap-0.5 text-meta-sm font-semibold px-1.5 py-0.5 rounded bg-badge-success-bg text-badge-success">
        <CheckCircle2 className="size-3" />
        <Trans>Resolved</Trans>
      </span>
    );
  }
  return (
    <span className="shrink-0 inline-flex items-center gap-0.5 text-meta-sm font-semibold px-1.5 py-0.5 rounded bg-badge-warn-bg text-badge-warn">
      <Circle className="size-3" />
      <Trans>Unresolved</Trans>
    </span>
  );
}

// ─── 댓글 미읽음 배지 ─────────────────────────────────────────

/** 댓글 수 + 미읽음 도트를 함께 표시하는 공용 컴포넌트 */
export function CommentBadge({
  count, isUnread, variant = 'inline',
}: {
  count: number;
  isUnread?: boolean;
  variant?: 'inline' | 'icon';
}) {
  if (count <= 0) return null;

  if (variant === 'icon') {
    return (
      <span className="relative flex items-center gap-0.5">
        <MessageCircle className="size-3 opacity-50" />
        <span className="tabular-nums">{count}</span>
        {isUnread && (
          <span className="absolute -top-0.5 -right-1.5 size-1.5 rounded-full bg-primary animate-pulse" />
        )}
      </span>
    );
  }

  // variant === 'inline' — [3] 스타일
  return (
    <span className="relative shrink-0 text-meta-sm font-semibold tabular-nums">
      <span className={isUnread ? 'text-primary' : 'text-primary'}>
        [{count}]
      </span>
      {isUnread && (
        <span className="absolute -top-0.5 -right-1.5 size-1.5 rounded-full bg-primary animate-pulse" />
      )}
    </span>
  );
}

// ─── 스타일별 Props ───────────────────────────────────────────

export interface StyleRendererProps {
  posts: Post[];
  onPostClick: (post: Post) => void;
  totalPosts: number;
  currentPage: number;
  onPageChange?: (page: number) => void;
  /** 현재 상세 보기 중인 게시글 ID (배경 하이라이트용) */
  selectedPostId?: string | null;
  /** 게시판 유형 (notice일 때 고정 영역 시각 분리) */
  boardType?: BoardType;
  /** Issue 상태 변경 콜백 (LinearRenderer에서 사용) */
  onStatusChange?: (postId: string, newStatus: IssueStatus) => Promise<void>;
  /** 미읽음 댓글이 있는 게시글 ID 목록 */
  unreadPostIds?: Set<string>;
  /** 통지센터 알림(미읽음)이 있는 게시글 ID 목록 — 제목 옆 오렌지 점 */
  notifiedPostIds?: Set<string>;
}

/** 통지센터 알림 미읽음 점 — 게시글 제목 옆 (탭/통지센터 점과 동일 오렌지) */
export function NotifDot() {
  return <span className="inline-block size-1.5 rounded-full bg-primary shrink-0" aria-label="unread" />;
}

/** postId 로 자체 MyNotifs 구독 → 미읽음 board 알림 있으면 NotifDot.
 *  대시보드처럼 컴포넌트가 여러 개로 나뉘어 prop 전달이 번거로운 곳용. */
export function NotifDotFor({ postId }: { postId: string }) {
  const [has, setHas] = useState(false);
  useEffect(() => {
    const compute = () => setHas(
      MyNotifs.Notifications.some(n => n.isNew && n.source === 'board' && n.postId === postId),
    );
    compute();
    return MyNotifs.subscribeToNotificationUpdates(compute);
  }, [postId]);
  return has ? <NotifDot /> : null;
}
